import AmountCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/AmountCell';
import formatQuantity from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatQuantity';
import formatShortDate from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatShortDate';
import formatTokens from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatTokens';
import resolveReferenceLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveReferenceLabel';
import resolveSubjectLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveSubjectLabel';
import NameCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/NameCell';
import resolveAverageMultiple from '@UI/Pages/Administration/AiCosts/CostTabs/ItemCosts/Helpers/resolveAverageMultiple';
import formatEuros from '@UI/Helpers/formatEuros';
import formatAudioDuration from '@UI/Pages/Administration/AiCosts/Helpers/formatAudioDuration';
import formatCount from '@UI/Pages/Administration/AiCosts/Helpers/formatCount';
import type AiCostBreakdownListView from '@UI/Store/AiCosts/Views/AiCostBreakdownListView';
import type AiCostBreakdownRowView from '@UI/Store/AiCosts/Views/AiCostBreakdownRowView';
import type AiCostSummaryView from '@UI/Store/AiCosts/Views/AiCostSummaryView';
import costTableStyles from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/costTableStyles';
import * as React from 'react';
import { Column, DataTable } from '@verclam/ui-kit';

export type ItemDimension = 'documents' | 'interviews';

type Props = {
	readonly list: AiCostBreakdownListView;
	readonly dimension: ItemDimension;
	readonly total: AiCostSummaryView | null;
	readonly reference: AiCostSummaryView | null;
};

/** One line per document or audio, with the Total of the period in the footer. */
const ItemCostsTable: React.FC<Props> = ({ list, dimension, total, reference }) => {
	const isAudio = dimension === 'interviews';
	const referenceAverage =
		(isAudio ? reference?.audio.averageCost : reference?.documents.averageCost) ?? null;
	const firstRow = list.list[0];
	const hasTokens = (firstRow?.quantities ?? null) !== null;
	const hasCalls = (firstRow?.callCount ?? null) !== null;
	const totalPart = total === null ? null : isAudio ? total.audio : total.documents;

	const renderDate = React.useCallback(
		(row: AiCostBreakdownRowView) => <NameCell title={formatShortDate(row.processedAt)} />,
		[],
	);

	const renderName = React.useCallback(
		(row: AiCostBreakdownRowView) => {
			const subject = resolveSubjectLabel(dimension, row);

			return <NameCell title={subject.title} isMuted={subject.isMuted} />;
		},
		[dimension],
	);

	const renderDuration = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<AmountCell label={formatAudioDuration(row.audio.durationSeconds)} />
		),
		[],
	);

	const renderUser = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<NameCell
				title={resolveReferenceLabel(row.user, 'Sans utilisateur', 'Utilisateur supprimé')}
				isMuted={row.user === null || row.user.label === null}
			/>
		),
		[],
	);

	const renderFacility = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<NameCell
				title={resolveReferenceLabel(row.facility, '—', 'Agence supprimée')}
				isMuted={row.facility !== null && row.facility.label === null}
			/>
		),
		[],
	);

	const renderCost = React.useCallback(
		(row: AiCostBreakdownRowView) => {
			if (row.unpricedItemCount > 0) {
				return <AmountCell label="Sans tarif" detail="non compté dans le total" />;
			}

			return (
				<AmountCell
					label={formatEuros(row.costAmount)}
					isBold
					chip={resolveAverageMultiple(row.costAmount, referenceAverage)}
				/>
			);
		},
		[referenceAverage],
	);

	const renderTokens = React.useCallback(
		(row: AiCostBreakdownRowView) => (
			<AmountCell label={formatTokens(row.quantities?.totalTokens ?? null)} />
		),
		[],
	);

	const renderCalls = React.useCallback(
		(row: AiCostBreakdownRowView) => <AmountCell label={formatCount(row.callCount)} />,
		[],
	);

	return (
		<DataTable
			value={list.list}
			ariaLabel={isAudio ? 'Coûts par audio' : 'Coûts par document'}
			pt={costTableStyles.pt}
		>
			<Column
				header="Date"
				body={renderDate}
				footer={total === null ? undefined : <NameCell title="Total" />}
			/>
			<Column
				header={isAudio ? 'Audio' : 'Document'}
				body={renderName}
				footer={
					totalPart === null ? undefined : (
						<NameCell
							title={
								isAudio
									? formatQuantity(totalPart.count, 'audio', 'audios')
									: formatQuantity(totalPart.count, 'document', 'documents')
							}
						/>
					)
				}
			/>
			<Column
				header="Durée"
				align="right"
				hidden={!isAudio}
				body={renderDuration}
				footer={
					total === null ? undefined : (
						<AmountCell label={formatAudioDuration(total.audio.durationSeconds)} />
					)
				}
			/>
			<Column header="Utilisateur" body={renderUser} />
			<Column header="Agence" body={renderFacility} />
			<Column
				header="Coût"
				align="right"
				body={renderCost}
				footer={
					totalPart === null ? undefined : (
						<AmountCell
							label={formatEuros(totalPart.costAmount)}
							isBold
							detail={
								totalPart.unpricedCount > 0
									? `hors ${formatCount(totalPart.unpricedCount)} sans tarif`
									: null
							}
						/>
					)
				}
			/>
			<Column header="Jetons" align="right" hidden={!hasTokens} body={renderTokens} />
			<Column header="Appels" align="right" hidden={!hasCalls} body={renderCalls} />
		</DataTable>
	);
};

export default ItemCostsTable;
